/**
 * Date-range presets for staff filter bars. Weeks run Monday to Sunday, as on the session tiles
 * (`SessionService.startOfWeek`).
 *
 * Terms follow the English school year: Autumn is 1 September to 31 December, Spring is 1 January to
 * 31 March, and Summer is 1 April to 31 August. Real term dates move with Easter and differ between
 * local authorities, so the preset covers the whole of each term's months. It is a quick range, not a
 * term calendar.
 */

export type DatePreset = 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'thisTerm';

export interface DatePresetOption {
  preset: DatePreset;
  label: string;
}

export const DATE_PRESET_OPTIONS: DatePresetOption[] = [
  { preset: 'thisWeek', label: 'This week' },
  { preset: 'lastWeek', label: 'Last week' },
  { preset: 'thisMonth', label: 'This month' },
  { preset: 'lastMonth', label: 'Last month' },
  { preset: 'thisTerm', label: 'This term' },
];

/** An inclusive date range as `yyyy-MM-dd` strings, the value an `<input type="date">` takes. */
export interface DateRange {
  from: string;
  to: string;
}

export function isDatePreset(value: unknown): value is DatePreset {
  return DATE_PRESET_OPTIONS.some(o => o.preset === value);
}

/** Resolves a preset against `today` (local time). */
export function resolveDatePreset(preset: DatePreset, today: Date = new Date()): DateRange {
  const day = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  switch (preset) {
    case 'thisWeek': {
      const monday = startOfWeek(day);
      return range(monday, addDays(monday, 6));
    }
    case 'lastWeek': {
      const monday = addDays(startOfWeek(day), -7);
      return range(monday, addDays(monday, 6));
    }
    case 'thisMonth':
      return range(new Date(day.getFullYear(), day.getMonth(), 1), new Date(day.getFullYear(), day.getMonth() + 1, 0));
    case 'lastMonth':
      return range(new Date(day.getFullYear(), day.getMonth() - 1, 1), new Date(day.getFullYear(), day.getMonth(), 0));
    case 'thisTerm':
      return termContaining(day);
  }
}

function termContaining(day: Date): DateRange {
  const year = day.getFullYear();
  const month = day.getMonth(); // 0 = January
  if (month >= 8) {
    return range(new Date(year, 8, 1), new Date(year, 11, 31));
  }
  if (month <= 2) {
    return range(new Date(year, 0, 1), new Date(year, 2, 31));
  }
  return range(new Date(year, 3, 1), new Date(year, 7, 31));
}

function startOfWeek(day: Date): Date {
  const offset = (day.getDay() + 6) % 7; // days since Monday
  return addDays(day, -offset);
}

function addDays(day: Date, days: number): Date {
  return new Date(day.getFullYear(), day.getMonth(), day.getDate() + days);
}

function range(from: Date, to: Date): DateRange {
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

/** A local date as `yyyy-MM-dd`. */
export function toIsoDate(day: Date): string {
  const mm = String(day.getMonth() + 1).padStart(2, '0');
  const dd = String(day.getDate()).padStart(2, '0');
  return `${day.getFullYear()}-${mm}-${dd}`;
}

/** `yyyy-MM-dd` as a local-midnight Date, or null when it does not parse. */
export function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  if (!match) {
    return null;
  }
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return isNaN(date.getTime()) ? null : date;
}
